<template>
    <form
        class="flex flex-col gap-y-4"
        @submit="handleSubmitCheckoutForm"
    >
        <billing-address
            v-model:editing="editingBillingAddress"
            :customer="primaryCustomer"
            :is-full-address="fullAddressRequired"
            :title="t('checkout.billingAddress')"
        />

        <checkout-section-separator
            v-if="requiresRecurringToken && readyForRecurringToken"
        />

        <recurring-information
            v-if="requiresRecurringToken && readyForRecurringToken"
            ref="recurringInformationRef"
            v-model:editing="editingRecurringInformation"
            :customer="primaryCustomer"
            :facility="facility"
            :recurring-total="recurringTotal"
            :payment-method-input="paymentMethodInput"
        />

        <checkout-section-separator
            v-if="requiresRecurringToken && readyForRecurringToken"
        />

        <document-section
            v-if="!requiresRecurringToken || readyForRecurringToken"
            :contract="contract"
            :is-signing-document="isPending || isValidating"
            balance-due-mode
            :invalid-initials="invalidInitials"
            :invalid-signatures="invalidSignatures"
            @inline-checkout-document="handleInlineCheckoutDocument"
            @inline-checkout="handleInlineCheckout"
        />

        <div
            v-if="showPleaseFixMessage"
            class="text-red-600 mt-4 font-medium"
        >
            {{ t('checkout.pleaseFixErrorsInRedAbove') }}
        </div>

        <div v-if="requiresRecurringToken && !readyForRecurringToken">
            <prime-button
                :label="
                    requiresRecurringToken
                        ? t('checkout.continueToPayment')
                        : t('checkout.continueToTermsAndConditions')
                "
                type="submit"
                class="w-full"
                :loading="isValidating"
            />
        </div>
    </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { object } from 'yup'
import { useForm } from 'vee-validate'
import { useRpQuery, UserReportableError } from '~/composables/graphql'
import { useMutation } from '@tanstack/vue-query'
import type {
    CheckoutRecurringInput,
    CommitCheckoutInput,
    DocumentInput
} from '~/resources/graphql/graphql'
import { computed, onMounted, ref, toRef, watch } from 'vue'
import { type FragmentType, graphql } from '~/resources/graphql'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'
import { GraphQLRequestError } from 'awesome-graphql-client'

import {
    CheckoutFormCheckoutFragment,
    CheckoutFormQueryFragment,
    useCheckoutForm
} from '~/components/Checkout/Forms/use-checkout-form'

import BillingAddress, {
    BillingAddressInitialValues,
    BillingAddressSchema
} from '~/components/Checkout/Sections/BillingAddress.vue'
import RecurringInformation, {
    RecurringInformationInitialValues,
    RecurringInformationSchema
} from '~/components/Checkout/Sections/RecurringInformation.vue'
import DocumentSection, {
    DocumentSectionContractFragment
} from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import { useKioskSuccess } from '~/composables/use-kiosk-success'
import { useShowAlert } from '#imports'
import CheckoutSectionSeparator from '~/components/Checkout/Forms/CheckoutSectionSeparator.vue'
import { checkoutRecurringInput } from '~/components/Checkout/Forms/checkout-helpers'
import {
    PaymentMethodInputPlanContractFragment,
    useGuestPaymentMethodInput
} from '~/components/Checkout/Sections/use-payment-method-input'
import PrimeButton from 'primevue/button'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    query: FragmentType<typeof CheckoutFormQueryFragment>
    checkout: FragmentType<typeof CheckoutFormCheckoutFragment>
    contract: FragmentType<typeof DocumentSectionContractFragment> &
        FragmentType<typeof PaymentMethodInputPlanContractFragment>
}>()

const emit = defineEmits<{
    (event: 'checkoutStatusChanged'): void
}>()

const { t } = useI18n({ useScope: 'global' })

const {
    checkoutId,

    primaryCustomer,
    facility,

    fullAddressRequired,
    requiresCustomerInformation,

    editingBillingAddress,
    editingRecurringInformation,

    recurringTotal,
    requiresRecurringToken,

    incrementProcessing,
    decrementProcessing
} = useCheckoutForm(toRef(props, 'query'), toRef(props, 'checkout'))

const schema = toTypedSchema(
    object({
        address: BillingAddressSchema(t),
        recurring: RecurringInformationSchema(t)
    })
)

const { handleSubmit, errors, setFieldError, validate, validateField } =
    useForm({
        validationSchema: schema,
        initialValues: {
            address: BillingAddressInitialValues(
                primaryCustomer.value,
                fullAddressRequired.value
            ),
            recurring: RecurringInformationInitialValues(
                requiresRecurringToken.value ? recurringTotal.value : null
            )
        }
    })

const showPleaseFixMessage = computed(() => {
    // No submission yet means don't show the error message
    if (submitCount.value < 1) {
        return false
    }

    // If we are on the pay now portion, show all errors
    if (readyForPayment.value) {
        return Object.keys(errors.value).length > 0
    }

    // Otherwise show errors with payment filtered out
    return (
        Object.keys(errors.value).filter(field => !field.startsWith('payment'))
            .length > 0
    )
})

const pendingCustomerInformation = ref(
    requiresCustomerInformation.value && requiresRecurringToken.value
)
const readyForRecurringToken = computed(
    () => pendingCustomerInformation.value === false
)
watch(editingBillingAddress, (newValue, oldValue) => {
    // If we require customer information for the payment methods, when the
    // customer information changes, we have to make a new session, so reset the form
    if (requiresCustomerInformation.value && requiresRecurringToken.value) {
        if (oldValue === false && newValue === true) {
            pendingCustomerInformation.value = true
        }
    }
})

const { paymentMethodInput, address } = useGuestPaymentMethodInput(
    toRef(props, 'contract')
)
onMounted(() => {
    if (requiresCustomerInformation.value) {
        // Only skip confirmation when the address section has nothing left to collect.
        if (address.value.postal !== '' && !editingBillingAddress.value) {
            pendingCustomerInformation.value = false
        }
    }
})

const pendingRecurringToken = ref(requiresRecurringToken.value)
const readyForPayment = computed(
    () =>
        pendingCustomerInformation.value === false &&
        pendingRecurringToken.value === false
)
const isValidating = ref(false)
// We use our own submit count because the continue to payment button does not submit the vee validate form
const submitCount = ref(0)

// When editing the recurring information section, reset the form value
watch(editingRecurringInformation, (newValue, oldValue) => {
    if (oldValue === false && newValue === true) {
        pendingRecurringToken.value = true
    }
})

// References to components allowing us to submit them
const recurringInformationRef = ref<InstanceType<typeof RecurringInformation>>()

// Main submit function
async function handleSubmitCheckoutForm(event?: SubmitEvent) {
    // Prevent form action
    event?.preventDefault()
    submitCount.value += 1

    // Lock every checkout control while validation and recurring payment collection
    // yield so earlier checkout sections cannot change underneath this submission.
    incrementProcessing()
    isValidating.value = true
    try {
        // Just get the customer information since the payment processor wants
        // this up front
        if (pendingCustomerInformation.value) {
            // Otherwise, validate the entire form
            const result = await validate()
            const isValid = Object.entries(result.results).every(
                ([key, value]) => {
                    return key.startsWith('recurring') || value.valid
                }
            )

            // TODO: Do we need to do this? Also clear this error so we don't
            //  flash red on the recurring error
            setFieldError('recurring', undefined)

            // Ready for recurring token or payment!
            if (isValid) {
                submitCount.value = 0 // reset submit count
                editingBillingAddress.value = false
                pendingCustomerInformation.value = false

                // Recurring payment method required?
                if (requiresRecurringToken.value) {
                    pendingRecurringToken.value = true
                    editingRecurringInformation.value = true
                }
            } else {
                // TODO: Find a way to scroll up
            }

            return
        }

        if (pendingRecurringToken.value) {
            // If recurring field is not valid, submit stripe form
            // Use mode: silent so we don't flash, the error message
            const { valid: validRecurring } = await validateField('recurring', {
                mode: 'silent'
            })
            if (!validRecurring) {
                await recurringInformationRef.value.submitRecurringForm()
            }
        }

        // Await vee-validate so the mutation callback acquires its processing
        // lock before this handler releases the validation lock in finally.
        await onSubmit(event)
        return
    } finally {
        // The successful submit callback owns a second lock until the mutation settles,
        // while validation failures and early returns release the only lock here.
        decrementProcessing()
        isValidating.value = false
    }
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const rpQuery = useRpQuery()
const { mutate, isPending } = useMutation({
    mutationFn: async (mutationData: CommitCheckoutInput) => {
        const response = await rpQuery(
            graphql(/** GraphQL */ `
                mutation InlineSubmitContract(
                    $input: InlineSubmitContractInput!
                ) {
                    inlineSubmitContract(input: $input) {
                        __typename
                        ... on InlineSubmitContractResult {
                            contract {
                                __typename
                                ... on PlanContract {
                                    referenceNumber
                                    planType
                                    customer {
                                        firstName
                                    }
                                }
                            }
                        }
                        ... on CheckoutProcessing {
                            checkoutId
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            {
                input: mutationData
            }
        )

        if (response.inlineSubmitContract === null) {
            throw new Error(
                t('checkout.inlineCheckoutError', {
                    code: 'CFKGBDM'
                })
            )
        }

        if (
            response.inlineSubmitContract.__typename ===
                'InlineSubmitContractResult' ||
            response.inlineSubmitContract.__typename === 'CheckoutProcessing' ||
            response.inlineSubmitContract.__typename === 'ValidationError'
        ) {
            return response.inlineSubmitContract
        }

        throw new Error()
    }
})

const kioskSuccessFunction = useKioskSuccess()

const showAlert = useShowAlert()
const savedDocumentInput = ref()
const onSubmit = handleSubmit(async values => {
    // Customer and Address
    const customer = {
        address: {
            ...values.address,
            // Wipe out the full address
            fullAddress: undefined
        }
    }

    // Recurring Token
    let recurring: CheckoutRecurringInput | null = null
    if (requiresRecurringToken.value) {
        recurring = checkoutRecurringInput(values.recurring)
    }

    incrementProcessing()

    mutate(
        {
            checkoutId: checkoutId.value,
            customer,
            recurring,
            document: savedDocumentInput.value
        },
        {
            onSuccess: data => {
                if (data.__typename === 'ValidationError') {
                    handleValidationErrors(
                        data.fieldErrors,
                        invalidInitials,
                        invalidSignatures
                    )

                    let messageBuilder = ''
                    data.fieldErrors.forEach(error => {
                        if (
                            error.path !== 'initials' &&
                            error.path !== 'signatures'
                        ) {
                            messageBuilder += error.message
                        }
                    })
                    if (messageBuilder !== '') {
                        showAlert(t('headsUp'), messageBuilder)
                    }
                } else if (data.__typename === 'CheckoutProcessing') {
                    // The embedded checkout query owns processing polling and durable result rendering.
                    emit('checkoutStatusChanged')
                } else {
                    kioskSuccessFunction({
                        includedInvoice: false,
                        wasGuest: false,
                        data: {
                            referenceNumber: data.contract.referenceNumber,
                            planType: data.contract.planType,
                            firstName: data.contract.customer.firstName
                        }
                    })
                }
            },
            onError(error) {
                if (
                    !(error instanceof UserReportableError) &&
                    !(error instanceof GraphQLRequestError)
                ) {
                    showAlert(t('headsUp'), String(error))
                }
            },
            onSettled() {
                decrementProcessing()
            }
        }
    )
})

function handleInlineCheckoutDocument(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
}

function handleInlineCheckout(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
    handleSubmitCheckoutForm().catch(error => {
        showAlert(t('headsUp'), String(error))
    })
}
</script>
